"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "next/navigation";
import Link from "next/link";
import { toast } from "sonner";
import dynamic from "next/dynamic";
import { ChevronLeft, ChevronRight, X } from "@/components/icons";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { useApplyGate, useLoginGate } from "@/features/auth/hooks/useLoginGate";
import { getApplicationVerificationError } from "@/lib/application-eligibility";
import { cn } from "@/lib/utils";

import BostadAbout from "@/features/ads/components/BostadAbout";
import CampaignBlock from "@/features/campaigns/components/CampaignBlock";
import BostadLandlord from "@/features/ads/components/BostadLandlord";
import ListingImagePlaceholder from "@/features/listings/components/ListingImagePlaceholder";
import ListingCardSkeleton from "@/features/listings/components/ListingCardSkeleton";
import SafeImage from "@/components/shared/SafeImage";
import { Skeleton } from "@/components/ui/skeleton";
import QueueListings from "@/features/ads/components/QueueListings";

import { listingService } from "@/features/listings/services/listing-service";
import {
  useApplyToListing,
  useFavorites,
  useListing,
  useMyApplications,
  useRequirementsProfile,
  useToggleFavorite,
} from "@/features/listings/hooks/useListings";
import {
  useJoinQueue,
  useMyQueues,
  useQueueCompany,
  useQueuesByCompany,
} from "@/features/queues/hooks/useQueues";
import { buildJoinedQueueIdSet } from "@/features/queues/services/queue-service";
import ApplicationModeNotice from "@/features/listings/components/ApplicationModeNotice";
import { RentalPolicyText } from "@/features/listings/components/RentalPolicyText";
import { documentTypesLabel } from "@/features/documents/document-type-label";
import {
  useApplicationQuota,
  useCompanyApplicantRequirements,
} from "@/features/companies/hooks/useCompanies";
import { applyErrorAction, applyErrorMessage, type ApplyErrorAction } from "@/features/listings/apply-errors";
import ApplyDocumentsDialog, { type SelectedDocument } from "@/features/documents/components/ApplyDocumentsDialog";
import { joinQueueErrorMessage } from "@/features/queues/join-errors";
import { ApplicantDetailsCompletionDialog } from "@/features/students/components/ApplicantDetailsCompletion";
import { ApiError } from "@/lib/api/client";
import {
  canRecordDemographicsForUser,
  demographicsService,
  getClientDeviceType,
  ignoreDemographicsRecordError,
} from "@/features/analytics/services/demographics-service";
import { qk } from "@/lib/query/keys";
import {
  ListingCardDTO,
  RequirementsProfileDTO,
} from "@/types/listing";
import { AdvertiserSummary } from "@/types";
import { useI18n } from "@/i18n/I18nProvider";
import { localizedText } from "@/i18n/text";

const ListingsMap = dynamic(() => import("@/components/shared/map/ListingsMap"), {
  ssr: false,
  loading: () => (
    <div className="min-h-[300px] w-full rounded-2xl bg-gray-100 animate-pulse sm:min-h-[360px] sm:rounded-3xl lg:min-h-[420px]" aria-hidden />
  ),
});

const DETAIL_PAGE_CONTAINER_CLASS =
  "mx-auto min-h-screen w-full max-w-7xl bg-white px-4 pb-10 pt-4 sm:px-6 sm:pb-12 lg:px-8 lg:pt-8";
const NEARBY_LISTINGS_PAGE_SIZE = 6;
const NEARBY_LISTINGS_FETCH_SIZE = 60;

// ─── Lightbox ────────────────────────────────────────────────────────────────
function Lightbox({
  images,
  startIndex,
  onClose,
}: {
  images: string[];
  startIndex: number;
  onClose: () => void;
}) {
  const { locale } = useI18n();
  const [current, setCurrent] = useState(startIndex);

  // Close on Escape, navigate with arrow keys
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setCurrent((c) => (c + 1) % images.length);
      if (e.key === "ArrowLeft") setCurrent((c) => (c - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [images.length, onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 px-3 py-16 sm:px-6"
      onClick={onClose}
    >
      {/* Close */}
      <button
        className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 sm:right-4 sm:top-4"
        onClick={onClose}
        aria-label={localizedText(locale, "Stäng", "Close")}
      >
        <X className="h-5 w-5" />
      </button>

      {/* Counter */}
      <span className="absolute top-4 left-1/2 -translate-x-1/2 text-white/70 text-sm font-medium">
        {current + 1} / {images.length}
      </span>

      {/* Prev */}
      {images.length > 1 && (
        <button
          className="absolute left-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 sm:left-4 sm:h-12 sm:w-12"
          onClick={(e) => { e.stopPropagation(); setCurrent((c) => (c - 1 + images.length) % images.length); }}
          aria-label={localizedText(locale, "Föregående bild", "Previous image")}
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}

      {/* Image */}
      <div className="max-h-[calc(100vh-9rem)] max-w-[94vw]" onClick={(e) => e.stopPropagation()}>
        <SafeImage
          src={images[current]}
          alt={localizedText(locale, `Bild ${current + 1}`, `Image ${current + 1}`)}
          width={1600}
          height={1000}
          sizes="94vw"
          className="max-h-[calc(100vh-9rem)] max-w-[94vw] rounded-xl object-contain shadow-2xl"
          fallback={
            <div className="aspect-[16/10] w-[min(94vw,860px)] overflow-hidden rounded-xl shadow-2xl">
              <ListingImagePlaceholder />
            </div>
          }
        />
      </div>

      {/* Next */}
      {images.length > 1 && (
        <button
          className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 sm:right-4 sm:h-12 sm:w-12"
          onClick={(e) => { e.stopPropagation(); setCurrent((c) => (c + 1) % images.length); }}
          aria-label={localizedText(locale, "Nästa bild", "Next image")}
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="absolute bottom-3 left-1/2 flex max-w-[94vw] -translate-x-1/2 gap-2 overflow-x-auto px-2 pb-1 sm:bottom-4">
          {images.map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
              className={`relative shrink-0 h-14 w-20 overflow-hidden rounded-lg border-2 transition ${
                i === current ? "border-white opacity-100" : "border-transparent opacity-50 hover:opacity-75"
              }`}
            >
              <SafeImage
                src={src}
                alt=""
                fill
                sizes="80px"
                className="object-cover object-center"
                fallback={<ListingImagePlaceholder className="absolute inset-0" />}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const MAX_PREVIEW_IMAGES = 3;
const PREVIEW_IMAGE_CLASS =
  "absolute inset-0 block !h-full !w-full !max-w-none scale-[1.025] object-cover object-center transition duration-300 group-hover:scale-[1.04]";

// ─── Top 3-image preview grid ────────────────────────────────────────────────
function ImagePreviewGrid({
  images,
  onImageClick,
}: {
  images: string[];
  onImageClick: (index: number) => void;
}) {
  const { locale } = useI18n();

  if (images.length === 0) {
    return (
      <div className="relative aspect-[16/10] w-full overflow-hidden rounded-2xl sm:aspect-[16/9] lg:aspect-[16/8] lg:rounded-3xl">
        <ListingImagePlaceholder className="absolute inset-0" />
      </div>
    );
  }

  const shown = images.slice(0, MAX_PREVIEW_IMAGES);

  if (shown.length === 1) {
    return (
      <button
        type="button"
        className="group relative block aspect-[16/10] w-full overflow-hidden rounded-2xl bg-gray-100 outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 sm:aspect-[16/9] lg:aspect-[16/8] lg:rounded-3xl"
        onClick={() => onImageClick(0)}
      >
        <SafeImage
          src={shown[0]}
          alt={localizedText(locale, "Bild 1", "Image 1")}
          fill
          priority
          sizes="(max-width: 1024px) 100vw, 1280px"
          className={PREVIEW_IMAGE_CLASS}
          fallback={<ListingImagePlaceholder className="absolute inset-0" />}
        />
        <div className="absolute inset-0 bg-black/0 transition group-hover:bg-black/10" />
      </button>
    );
  }

  // 2-3 images: main left + side thumbnails
  const side = shown.slice(1);
  return (
    <div className="grid w-full gap-2 lg:h-[clamp(360px,42vw,520px)] lg:grid-cols-[minmax(0,1.65fr)_minmax(220px,0.75fr)]">
      {/* Main image */}
      <button
        type="button"
        className="group relative aspect-[16/10] overflow-hidden rounded-2xl bg-gray-100 outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 sm:aspect-[16/9] lg:h-full lg:aspect-auto lg:rounded-l-3xl lg:rounded-r-none"
        onClick={() => onImageClick(0)}
      >
        <SafeImage
          src={shown[0]}
          alt={localizedText(locale, "Bild 1", "Image 1")}
          fill
          priority
          sizes="(max-width: 1024px) 100vw, 850px"
          className={PREVIEW_IMAGE_CLASS}
          fallback={<ListingImagePlaceholder className="absolute inset-0" />}
        />
        <div className="absolute inset-0 bg-black/0 transition group-hover:bg-black/10" />
      </button>

      {/* Side thumbnails */}
      <div
        className={cn(
          "grid gap-2 lg:h-full lg:grid-cols-1",
          side.length === 1 ? "grid-cols-1" : "grid-cols-2 lg:grid-rows-2",
        )}
      >
        {side.map((src, i) => {
          const isLast = i === side.length - 1 && images.length > MAX_PREVIEW_IMAGES;
          const isFirst = i === 0;
          const isEnd = i === side.length - 1;
          return (
            <button
              key={`${src}-${i + 1}`}
              type="button"
              className={cn(
                "group relative aspect-[16/10] min-h-[118px] overflow-hidden rounded-2xl bg-gray-100 outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 sm:aspect-[16/9] lg:h-full lg:min-h-0 lg:aspect-auto",
                side.length > 1 && isFirst && "lg:rounded-bl-none lg:rounded-br-none lg:rounded-tl-none lg:rounded-tr-3xl",
                side.length > 1 && isEnd && "lg:rounded-bl-none lg:rounded-br-3xl lg:rounded-tl-none lg:rounded-tr-none",
                side.length === 1 && "lg:rounded-l-none lg:rounded-r-3xl"
              )}
              onClick={() => onImageClick(i + 1)}
            >
              <SafeImage
                src={src}
                alt={localizedText(locale, `Bild ${i + 2}`, `Image ${i + 2}`)}
                fill
                sizes="(max-width: 1024px) 50vw, 380px"
                className={PREVIEW_IMAGE_CLASS}
                fallback={<ListingImagePlaceholder className="absolute inset-0" />}
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition group-hover:bg-black/10">
                {isLast && (
                  <span className="rounded-full bg-black/55 px-3 py-1.5 text-sm font-semibold text-white shadow-sm sm:text-base">
                    +{images.length - MAX_PREVIEW_IMAGES} {localizedText(locale, "fler", "more")}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RequirementsProfileSection({
  profile,
  loading,
}: {
  profile: RequirementsProfileDTO | null;
  loading: boolean;
}) {
  const { locale } = useI18n();
  if (loading) {
    return (
      <section className="rounded-2xl border border-black/5 bg-white/80 p-4 shadow-[0_18px_45px_rgba(0,0,0,0.05)] sm:rounded-3xl sm:p-6">
        <h2 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
          {localizedText(locale, "Kravprofil", "Requirements profile")}
        </h2>
        <p className="mt-3 text-sm text-gray-500">
          {localizedText(locale, "Hämtar kravprofil...", "Loading requirements profile...")}
        </p>
      </section>
    );
  }

  if (!profile) return null;

  const ageRange =
    profile.minAge || profile.maxAge
      ? [
          profile.minAge ? `${localizedText(locale, "Min", "Min")} ${profile.minAge} ${localizedText(locale, "år", "years")}` : null,
          profile.maxAge ? `${localizedText(locale, "Max", "Max")} ${profile.maxAge} ${localizedText(locale, "år", "years")}` : null,
        ]
          .filter(Boolean)
          .join(" / ")
      : null;

  return (
    <section className="rounded-2xl border border-black/5 bg-white/80 p-4 shadow-[0_18px_45px_rgba(0,0,0,0.05)] sm:rounded-3xl sm:p-6">
      <div className="flex flex-col gap-2">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-green-900">
          {localizedText(locale, "Ansökningskrav", "Application requirements")}
        </p>
        <h2 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
          {profile.title || localizedText(locale, "Kravprofil", "Requirements profile")}
        </h2>
        {ageRange && <p className="text-sm font-medium text-gray-700">{ageRange}</p>}
        {profile.description && (
          <RentalPolicyText
            text={profile.description}
            locale={locale}
            className="mt-2 max-w-3xl text-sm leading-6 text-gray-700"
          />
        )}
      </div>

      {profile.requiredDocuments?.length ? (
        <div className="mt-5 grid gap-3 min-[520px]:grid-cols-2">
          {profile.requiredDocuments.map((document, index) => (
            <div
              className="min-w-0 rounded-2xl border border-gray-200 bg-white px-4 py-3"
              key={`${document.caption ?? "document"}-${index}`}
            >
              <p className="text-sm font-semibold text-gray-900">
                {document.caption ?? localizedText(locale, "Dokument", "Document")}
              </p>
              <p className="mt-1 break-words text-xs font-medium uppercase tracking-[0.08em] text-gray-500">
                {documentTypesLabel(document.validTypes, locale) ||
                  localizedText(locale, "Valfri filtyp", "Any file type")}
              </p>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function ListingDetailPage() {
  const params = useParams<{ id: string }>();
  const listingId = params?.id;
  const { user } = useAuth();
  const { locale } = useI18n();

  // Per-id increment + demographics guards. These are fire-and-forget,
  // not server state, so they stay as direct service calls inside an
  // effect with a Set-ref guard (same pattern as before).
  const detailedViewIncrementedIds = useRef<Set<string>>(new Set());
  const detailedViewDemographicsRecordedIds = useRef<Set<string>>(new Set());

  const requireLogin = useLoginGate();
  const requireApplyAccess = useApplyGate();

  // Local UI state — apply form, lightbox.
  const [applyError, setApplyError] = useState<string | null>(null);
  /** Where the student can go to fix the failure themselves, when there is such a place. */
  const [applyErrorLink, setApplyErrorLink] = useState<ApplyErrorAction | null>(null);
  /** The document dialog is the last step before an application is sent. */
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  /**
   * The applicant details the landlord requires and the account lacks, shown
   * as a step before the documents. Null once the student has saved them (or
   * never lacked any). Set from the landlord's published requirements when the
   * dialog opens, and from a PROFILE_INCOMPLETE answer if the backend knows
   * better than our cache.
   */
  const [completionFields, setCompletionFields] = useState<string[] | null>(null);
  const [applySuccess, setApplySuccess] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);


  // Utloggade och quick-register hanteras av useApplyGate (redirect); det
  // inline-meddelandet gäller bara studentkonton med ofullständig verifiering.
  const applicationVerificationError = useMemo(
    () =>
      user?.accountType === "quick_register"
        ? null
        : getApplicationVerificationError(user, "listing", locale),
    [locale, user]
  );

  // The listing detail — shared cache. If the user came from the search
  // page card, that page seeded list data but not detail; detail caches
  // its own slot keyed by id.
  const {
    data: listing,
    isLoading: loading,
    isError,
  } = useListing(listingId);
  const error = isError ? "Kunde inte ladda annonsen." : null;

  // Favorites + my-applications: both shared with other pages, so coming
  // from search or sparade hits the cache instead of re-fetching.
  const { data: favoritesData } = useFavorites();
  const favoriteIds = useMemo<Set<string>>(
    () => new Set((favoritesData ?? []).map((f) => f.id)),
    [favoritesData]
  );
  const toggleFavorite = useToggleFavorite();

  const { data: myApplications } = useMyApplications();
  const hasApplied = useMemo(
    () =>
      Boolean(
        listingId &&
          (myApplications ?? []).some((a: any) => a.listingId === listingId)
      ),
    [myApplications, listingId]
  );

  // Requirements profile — only when listing has one.
  const { data: requirementsProfile, isLoading: requirementsLoading } =
    useRequirementsProfile(listing?.requirementsProfileId);

  // Company profile (logo, name, description) for company-owned listings —
  // this is /companies/{id}, no queue data is involved.
  const companyId =
    listing && listing.ownerType.toLowerCase() === "company"
      ? listing.ownerId
      : null;
  const { data: company } = useQueueCompany(companyId);

  // Whether this listing needs the company's queue, and whether the student is
  // already in it. Both are needed before the apply button renders — learning
  // about the requirement from a refused application is the failure mode this
  // whole block exists to remove.
  const requiresQueue = listing?.applicationMode === "QUEUE_REQUIRED";

  // From the company's queues, not its profile: GET /companies/{id} answers with
  // CompanyDetailedDTO, which carries no queue id at all — reading one off it
  // was always undefined, so every queue-required listing claimed the queue was
  // closed. This endpoint is also the one filtered to open queues, so an empty
  // list genuinely means there is nothing to join.
  const { data: companyQueues, isLoading: companyQueuesLoading } =
    useQueuesByCompany(requiresQueue ? companyId : null);
  const companyQueueId = companyQueues?.[0]?.id
    ? String(companyQueues[0].id)
    : null;

  const { data: myQueues, isLoading: myQueuesLoading } = useMyQueues(
    { hydrated: false },
    { enabled: requiresQueue }
  );
  const joinQueue = useJoinQueue();

  // Null means "not known yet" — deliberately distinct from false, which would
  // tell a student who is already queuing to go and join again.
  const isInQueue = useMemo<boolean | null>(() => {
    if (!user || !requiresQueue) return null;
    // Unknown until both answers are in. Resolving early would state, as fact,
    // either that the student is outside the queue or that the queue is closed —
    // and the second one is what this block exists to stop getting wrong.
    if (myQueuesLoading || !myQueues || companyQueuesLoading) return null;
    if (!companyQueueId) return false;
    return buildJoinedQueueIdSet(myQueues).has(companyQueueId);
  }, [
    companyQueueId,
    companyQueuesLoading,
    myQueues,
    myQueuesLoading,
    requiresQueue,
    user,
  ]);

  const queueBlocksApply = requiresQueue && isInQueue === false;

  /**
   * The fields the landlord's registry lacks when a queue join was refused for
   * them; the join is retried once the student has saved them. Null while the
   * dialog is closed.
   */
  const [queueCompletionFields, setQueueCompletionFields] = useState<
    string[] | null
  >(null);

  // `retryOnIncomplete` is false for the automatic retry after the completion
  // form, so a second PROFILE_INCOMPLETE is reported rather than looping.
  const handleJoinQueue = useCallback((retryOnIncomplete = true) => {
    if (!companyQueueId || !requireApplyAccess()) return;
    if (applicationVerificationError) {
      setApplyError(applicationVerificationError);
      return;
    }

    setApplyError(null);
    joinQueue.mutate(companyQueueId, {
      onError: (err: unknown) => {
        if (
          retryOnIncomplete &&
          err instanceof ApiError &&
          err.code === "PROFILE_INCOMPLETE" &&
          err.missingFields.length > 0
        ) {
          setQueueCompletionFields(err.missingFields);
          return;
        }
        setApplyError(joinQueueErrorMessage(err, locale));
      },
    });
  }, [
    applicationVerificationError,
    companyQueueId,
    joinQueue,
    locale,
    requireApplyAccess,
  ]);

  const handleQueueCompleted = useCallback(() => {
    setQueueCompletionFields(null);
    handleJoinQueue(false);
  }, [handleJoinQueue]);

  // How many applications the student may still have running at this company,
  // counted across CampusLyan and the company's own portal. Only companies cap
  // applications, so private listings never fetch it.
  const { data: applicationQuota } = useApplicationQuota(companyId);
  // What the company's applicant registry requires and, for this student,
  // what their account still lacks — so the apply dialog can ask for it
  // before the application goes out rather than after it bounces.
  const { data: applicantRequirements } =
    useCompanyApplicantRequirements(companyId);
  const quotaExhausted = Boolean(
    applicationQuota?.limited &&
      applicationQuota.remaining !== null &&
      applicationQuota.remaining <= 0 &&
      !hasApplied
  );

  useEffect(() => {
    if (!listing?.id) return;

    if (!detailedViewIncrementedIds.current.has(listing.id)) {
      detailedViewIncrementedIds.current.add(listing.id);
      listingService
        .incrementViews(listing.id, "DETAILED")
        .catch((err) =>
          console.error("Failed to increment detailed view:", err)
        );
    }

    if (
      canRecordDemographicsForUser(user) &&
      !detailedViewDemographicsRecordedIds.current.has(listing.id)
    ) {
      detailedViewDemographicsRecordedIds.current.add(listing.id);
      demographicsService
        .recordListingViewWithCompanyScope(companyId, listing.id, {
          deviceType: getClientDeviceType(),
          viewType: "DETAILED",
          resultedInLike: favoriteIds.has(listing.id),
        })
        .catch(ignoreDemographicsRecordError);
    }
  }, [companyId, favoriteIds, listing?.id, user]);

  // Nearby listings — preserves the same two-step fetch fallback the old
  // code had (try city-filtered, then fall back to unfiltered if too few).
  // Wrapped in a single useQuery so the whole "nearby" view caches under
  // one key. Disabled until we have a listing.
  const nearbyQuery = useQuery<ListingCardDTO[]>({
    queryKey: qk.listings.nearby(
      listing?.id ?? null,
      listing?.city || listing?.area || null,
      NEARBY_LISTINGS_FETCH_SIZE
    ),
    enabled: Boolean(listing),
    staleTime: 60_000,
    queryFn: async () => {
      if (!listing) return [];

      const location = listing.city || listing.area || null;
      const nearbyResponse = await listingService.getAll(
        {
          page: 0,
          size: NEARBY_LISTINGS_FETCH_SIZE,
          city: location,
          seed: listing.id,
        }
      );

      const byId = new Map<string, ListingCardDTO>();
      const addCandidates = (items: ListingCardDTO[] = []) => {
        items.forEach((item) => {
          if (item.id !== listing.id && !byId.has(item.id)) {
            byId.set(item.id, item);
          }
        });
      };

      addCandidates(nearbyResponse.content);

      if (byId.size < NEARBY_LISTINGS_PAGE_SIZE) {
        const fallbackResponse = await listingService.getAll(
          {
            page: 0,
            size: NEARBY_LISTINGS_FETCH_SIZE,
            seed: listing.id,
          }
        );
        addCandidates(fallbackResponse.content);
      }

      return Array.from(byId.values());
    },
  });
  const nearbyListings = nearbyQuery.data ?? [];
  const nearbyLoading = nearbyQuery.isLoading;
  const nearbyError = nearbyQuery.isError ? localizedText(locale, "Kunde inte ladda fler bostäder.", "Could not load more homes.") : null;

  // Client-side pagination over the full nearby set (pulled in one query).
  // Reset to page 1 whenever the underlying listing or its location changes.
  const [nearbyListingsPage, setNearbyListingsPage] = useState(1);
  const nearbyTotalPages = useMemo(
    () =>
      Math.max(
        1,
        Math.ceil(nearbyListings.length / NEARBY_LISTINGS_PAGE_SIZE),
      ),
    [nearbyListings.length],
  );
  const nearbyCurrentPage = Math.min(
    Math.max(1, nearbyListingsPage),
    nearbyTotalPages,
  );
  const nearbyPageListings = useMemo(() => {
    const start = (nearbyCurrentPage - 1) * NEARBY_LISTINGS_PAGE_SIZE;
    return nearbyListings.slice(start, start + NEARBY_LISTINGS_PAGE_SIZE);
  }, [nearbyCurrentPage, nearbyListings]);

  useEffect(() => {
    setNearbyListingsPage(1);
  }, [listing?.id]);

  useEffect(() => {
    if (nearbyListingsPage > nearbyTotalPages) {
      setNearbyListingsPage(nearbyTotalPages);
    }
  }, [nearbyListingsPage, nearbyTotalPages]);

  const handleNearbyPageChange = useCallback((nextPage: number) => {
    setNearbyListingsPage(nextPage);
    if (typeof document !== "undefined") {
      document
        .getElementById("nearby-listings")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  // Apply mutation: invalidates myApplications on settle so hasApplied
  // flips true automatically when the cache re-syncs.
  const applyMutation = useApplyToListing();

  const handleFavoriteToggle = useCallback(
    async (id: string, isFav: boolean) => {
      if (!requireLogin() || !user) {
        return;
      }

      // Optimistic patch + rollback live inside useToggleFavorite. We keep
      // the demographics side effect here — it's fire-and-forget.
      toggleFavorite.mutate({ listingId: id, nextIsFavorite: isFav });

      if (isFav && canRecordDemographicsForUser(user)) {
        demographicsService
          .recordListingViewWithCompanyScope(companyId, id, {
            deviceType: getClientDeviceType(),
            viewType: "DETAILED",
            resultedInLike: true,
          })
          .catch(ignoreDemographicsRecordError);
      }
    },
    [companyId, user, toggleFavorite, requireLogin]
  );

  const openLightbox = useCallback((index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  // Fire-and-forget: increment view count + record demographics, once per
  // listing per session. Same Set-ref guard as before.
  const sendApplication = useCallback(
    (documents: SelectedDocument[], isPrivate = false) => {
      applyMutation.mutate(
        {
          listingId,
          message: localizedText(locale, "Hej! Jag är intresserad.", "Hi! I am interested."),
          isPrivate,
          documents,
        },
        {
          onSuccess: () => {
            setDocumentsDialogOpen(false);
            setApplySuccess(localizedText(locale, "Ansökan skickad!", "Application sent!"));
          },
          onError: (err: unknown) => {
            // The backend knows what the registry lacks better than our cached
            // requirements do. Swap the completion step into the still-open
            // dialog; the student's document choices survive, and they send
            // again themselves once the details are saved.
            if (
              !isPrivate &&
              err instanceof ApiError &&
              err.code === "PROFILE_INCOMPLETE" &&
              err.missingFields.length > 0
            ) {
              setCompletionFields(err.missingFields);
              return;
            }
            setDocumentsDialogOpen(false);
            setApplyError(applyErrorMessage(err, locale));
            setApplyErrorLink(applyErrorAction(err, locale));
          },
        }
      );
    },
    [applyMutation, listingId, locale]
  );

  const closeDocumentsDialog = useCallback(() => {
    setDocumentsDialogOpen(false);
    setCompletionFields(null);
  }, []);

  const handleApply = useCallback(() => {
    if (!listingId || !listing) return;
    if (!requireApplyAccess()) {
      return;
    }

    if (applicationVerificationError) {
      setApplyError(applicationVerificationError);
      return;
    }

    setApplyError(null);
    setApplySuccess(null);
    setApplyErrorLink(null);

    // A private landlord has no requirements profile and no document registry,
    // so there is nothing to choose and the application goes straight out.
    const isCompany = listing.ownerType.toLowerCase() === "company";
    if (isCompany) {
      // Ask for what the landlord requires first, when we already know it is
      // missing. If the requirements have not loaded yet the documents step
      // opens directly and a PROFILE_INCOMPLETE answer brings the form up then.
      const missing = applicantRequirements?.missingFields ?? [];
      setCompletionFields(missing.length > 0 ? missing : null);
      setDocumentsDialogOpen(true);
      return;
    }

    sendApplication([], true);
  }, [
    applicantRequirements,
    applicationVerificationError,
    listingId,
    listing,
    requireApplyAccess,
    locale,
    sendApplication
  ]);
  const applying = applyMutation.isPending;

  const galleryImages = useMemo(() => listing?.imageUrls || [], [listing]);

  const advertiser: (AdvertiserSummary & { companyPageUrl?: string }) | null = useMemo(() => {
    if (!listing) return null;
    const isCompany = listing.ownerType.toLowerCase() === "company";
    return {
      id: listing.ownerId,
      type: isCompany ? "company" : "private_landlord",
      displayName: company?.name || listing.ownerName,
      logoUrl: company?.logoUrl || listing.ownerLogoUrl || null,
      bannerUrl: null,
      city: null,
      rating: null,
      website: null,
      description: company?.description ?? null,
      phone: null,
      contactEmail: null,
      contactPhone: null,
      // Deliberately not the property system: which integration a company runs
      // is our plumbing, and the raw enum name ("HOGIA_LANDLORD") meant nothing
      // to a student even before it read backwards against the product names.
      contactNote: null,
      subtitle: isCompany
        ? localizedText(locale, "Företag", "Company")
        : localizedText(locale, "Bostadsaktör", "Housing provider"),
      companyPageUrl: isCompany ? `/company/${listing.ownerId}` : undefined,
    };
  }, [company, listing, locale]);

  const mapListings = useMemo<ListingCardDTO[]>(() => {
    if (!listing) return [];
    return [{
      id: listing.id,
      title: listing.title,
      imageUrl: listing.imageUrls?.[0] || "",
      location: `${listing.area}, ${listing.city}`,
      rent: listing.rent,
      dwellingType: listing.dwellingType,
      rooms: listing.rooms,
      sizeM2: listing.sizeM2 || 0,
      tags: listing.tags,
      hostType: listing.ownerType,
      verifiedHost: listing.verifiedOwner,
      lat: listing.lat,
      lng: listing.lng,
    }];
  }, [listing]);

  if (loading) {
    return (
      <div className={DETAIL_PAGE_CONTAINER_CLASS} aria-busy="true">
        <span className="sr-only" role="status">
          {localizedText(locale, "Laddar annons...", "Loading listing...")}
        </span>
        <div className="flex w-full flex-col gap-6 sm:gap-8 lg:gap-10" aria-hidden="true">
          {/* Skugga av bildgrid + rubrik/pris + innehållsblock */}
          <Skeleton className="aspect-[16/10] w-full rounded-2xl motion-reduce:animate-none sm:aspect-[16/9] lg:aspect-[16/8] lg:rounded-3xl" />
          <div className="flex flex-col gap-3">
            <Skeleton className="h-4 w-32 motion-reduce:animate-none" />
            <Skeleton className="h-8 w-2/3 max-w-md motion-reduce:animate-none" />
            <Skeleton className="h-5 w-40 motion-reduce:animate-none" />
          </div>
          <div className="flex flex-col gap-2.5">
            <Skeleton className="h-4 w-full max-w-2xl motion-reduce:animate-none" />
            <Skeleton className="h-4 w-full max-w-xl motion-reduce:animate-none" />
            <Skeleton className="h-4 w-2/3 max-w-lg motion-reduce:animate-none" />
          </div>
          <Skeleton className="h-[300px] w-full rounded-2xl motion-reduce:animate-none sm:h-[360px] sm:rounded-3xl lg:h-[420px]" />
        </div>
      </div>
    );
  }

  if (error || !listing || !advertiser) {
    return (
      <div className={DETAIL_PAGE_CONTAINER_CLASS}>
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-6 text-center text-red-800">
          {error ?? localizedText(locale, "Annonsen kunde inte hittas.", "The listing could not be found.")}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Lightbox — rendered outside the scroll container, no z-index conflicts */}
      {lightboxOpen && (
        <Lightbox
          images={galleryImages}
          startIndex={lightboxIndex}
          onClose={closeLightbox}
        />
      )}

      <div className={DETAIL_PAGE_CONTAINER_CLASS}>
        <div className="flex w-full flex-col gap-6 sm:gap-8 lg:gap-10">

          {/* Feedback messages */}
          {applySuccess && (
            <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
              {applySuccess}
            </div>
          )}
          {listingId && (
            <ApplyDocumentsDialog
              listingId={listingId}
              open={documentsDialogOpen}
              onCancel={closeDocumentsDialog}
              onSubmit={(documents) => sendApplication(documents)}
              submitting={applyMutation.isPending}
              completion={
                completionFields
                  ? {
                      fieldNames: completionFields,
                      companyName: company?.name ?? listing.ownerName,
                      // The form has already saved the account and refetched
                      // the requirements; all that is left is to move on to
                      // the documents step and let the student send.
                      onCompleted: () => setCompletionFields(null),
                    }
                  : null
              }
            />
          )}
          {applyError && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {applyError}
              {applyErrorLink && (
                <Link
                  href={applyErrorLink.href}
                  className="mt-2 block font-medium underline underline-offset-2"
                >
                  {applyErrorLink.label}
                </Link>
              )}
            </div>
          )}
          {applicationVerificationError && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {applicationVerificationError}
            </div>
          )}

          {/* The company's cap on simultaneous applications. Shown before the
              student clicks rather than only as an error afterwards — the count
              includes applications they made in the company's own portal, so
              running out can otherwise come as a complete surprise. */}
          {applicationQuota?.limited && !hasApplied && (
            <div
              className={
                quotaExhausted
                  ? "rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                  : "rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700"
              }
            >
              {quotaExhausted
                ? localizedText(
                    locale,
                    `Du har ${applicationQuota.active} av ${applicationQuota.max} aktiva ansökningar hos ${applicationQuota.companyName} — så många de tillåter samtidigt. Dra tillbaka en ansökan för att kunna söka den här bostaden.`,
                    `You have ${applicationQuota.active} of ${applicationQuota.max} active applications with ${applicationQuota.companyName} — as many as they allow at once. Withdraw one to apply for this home.`
                  )
                : localizedText(
                    locale,
                    `Du har ${applicationQuota.active} av ${applicationQuota.max} aktiva ansökningar hos ${applicationQuota.companyName}.`,
                    `You have ${applicationQuota.active} of ${applicationQuota.max} active applications with ${applicationQuota.companyName}.`
                  )}

              {/* Only true where the company's own system takes applications.
                  Saying it for a company whose applications only exist here
                  would send the student looking for a portal that has none. */}
              {applicationQuota.externallyManaged && (
                <span className="block">
                  {localizedText(
                    locale,
                    "Antalet räknar även ansökningar du gjort direkt hos företaget.",
                    "The count also includes applications you made directly with the company."
                  )}
                </span>
              )}
            </div>
          )}

          {/* How this home is applied for, and — when a queue is required —
              whether the student already satisfies it. */}
          {!hasApplied && (
            <ApplicationModeNotice
              canJoinQueue={Boolean(companyQueueId)}
              companyName={company?.name ?? listing.ownerName}
              isInQueue={isInQueue}
              isJoining={joinQueue.isPending}
              mode={listing.applicationMode}
              onJoinQueue={() => handleJoinQueue()}
            />
          )}

          <ApplicantDetailsCompletionDialog
            open={queueCompletionFields !== null}
            onOpenChange={(open) => {
              if (!open) setQueueCompletionFields(null);
            }}
            fieldNames={queueCompletionFields ?? []}
            companyName={company?.name ?? listing.ownerName}
            onCompleted={handleQueueCompleted}
          />

          {/* 1. Top image preview grid */}
          <ImagePreviewGrid images={galleryImages} onImageClick={openLightbox} />

          {/* 2. About / listing info */}
          <BostadAbout
            listing={listing}
            isFavorite={favoriteIds.has(listing.id)}
            onFavoriteToggle={handleFavoriteToggle}
            onApplyClick={handleApply}
            applyDisabled={
              applying ||
              hasApplied ||
              quotaExhausted ||
              queueBlocksApply ||
              Boolean(applicationVerificationError)
            }
            hasApplied={hasApplied}
          />

          {/* Directly under the price it qualifies — the conditions attached to
              a discount have to be readable before the student applies, not
              further down the page than the apply button. */}
          <CampaignBlock campaigns={listing.campaigns} />

          <RequirementsProfileSection
            profile={requirementsProfile ?? null}
            loading={requirementsLoading}
          />

          {/* 3. Map — own dedicated section */}
          <section className="w-full">
            <div className="h-[300px] w-full overflow-hidden rounded-2xl border border-black/5 shadow-[0_18px_45px_rgba(0,0,0,0.05)] sm:h-[360px] sm:rounded-3xl lg:h-[420px]">
              <ListingsMap
                listings={mapListings}
                className="h-full w-full"
                fillContainer
                showPopups={false}
              />
            </div>
          </section>

          {/* 4. Provider info */}
          <BostadLandlord advertiser={advertiser} />

          {/* 5. Nearby listings. (A second full slideshow used to sit here; it
              repeated the gallery above, which already opens the lightbox.) */}
          <section id="nearby-listings" className="scroll-mt-24 border-t border-gray-100 pt-6 sm:pt-8">
            {!nearbyLoading && !nearbyError && nearbyPageListings.length > 0 ? (
              <QueueListings
                listings={nearbyPageListings}
                title={localizedText(locale, "Fler bostäder i närheten", "More homes nearby")}
                page={nearbyCurrentPage}
                totalPages={nearbyTotalPages}
                onPageChange={handleNearbyPageChange}
              />
            ) : (
              <>
                <h2 className="mb-5 text-xl font-bold tracking-tight text-gray-900 sm:mb-6 sm:text-2xl">
                  {localizedText(locale, "Fler bostäder i närheten", "More homes nearby")}
                </h2>

                {nearbyLoading ? (
                  <div
                    className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-6 2xl:grid-cols-3"
                    aria-busy="true"
                  >
                    {Array.from({ length: 3 }, (_, index) => (
                      <div key={`nearby-skeleton-${index}`} className="flex w-full justify-center">
                        <ListingCardSkeleton />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className={`rounded-xl border border-dashed px-4 py-8 text-center sm:p-10 ${
                      nearbyError
                        ? "border-red-200 bg-red-50 text-red-700"
                        : "border-gray-300 bg-gray-50 text-gray-500"
                    }`}
                  >
                    {nearbyError
                      ? nearbyError
                      : localizedText(locale, "Inga fler bostäder hittades just nu.", "No more homes were found right now.")}
                  </div>
                )}
              </>
            )}
          </section>

        </div>
      </div>
    </>
  );
}
